import React, { useMemo, useState } from 'react';
import { ArrowLeft, Check, Lock } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
type MarketCode = 'AU' | 'SG' | 'US';
type LockedCode = 'EU' | 'NZ';
type ClaimVerdict = 'Go' | 'Review' | 'No go' | 'Not applicable';
type ClaimCompareRow = {
  claim: string;
  verdicts: Record<MarketCode, ClaimVerdict>;
};
const marketMeta: Record<MarketCode, {
  name: string;
  classification: string;
}> = {
  AU: {
    name: 'Australia',
    classification: 'Listed medicine (AUST L)'
  },
  SG: {
    name: 'Singapore',
    classification: 'Health supplement'
  },
  US: {
    name: 'United States',
    classification: 'Dietary supplement (DSHEA)'
  }
};
const lockedMarkets: LockedCode[] = ['EU', 'NZ'];
const selectableMarkets: MarketCode[] = ['AU', 'SG', 'US'];
const lockedTooltip = 'No assessment yet — create a dossier for this market to compare';
const supplementSummaries: Record<MarketCode, string> = {
  AU: '1 permitted · 4 with conditions · 5 not permitted',
  SG: '5 permitted · 3 with conditions · 1 not permitted · 1 not applicable',
  US: '9 permitted · 1 with conditions'
};
const supplementRows: ClaimCompareRow[] = [{
  claim: 'Supports immune function',
  verdicts: {
    AU: 'Review',
    SG: 'Go',
    US: 'Go'
  }
}, {
  claim: 'Promotes collagen formation',
  verdicts: {
    AU: 'Review',
    SG: 'Go',
    US: 'Go'
  }
}, {
  claim: 'Supports vision, red blood cell and bone formation',
  verdicts: {
    AU: 'Review',
    SG: 'Go',
    US: 'Go'
  }
}, {
  claim: 'Promotes growth and development',
  verdicts: {
    AU: 'Review',
    SG: 'Review',
    US: 'Go'
  }
}, {
  claim: 'Not intended to diagnose, treat or cure',
  verdicts: {
    AU: 'No go',
    SG: 'Not applicable',
    US: 'Go'
  }
}, {
  claim: 'Instead of the usual synthetic fillers',
  verdicts: {
    AU: 'No go',
    SG: 'Review',
    US: 'Review'
  }
}, {
  claim: 'Satisfaction guaranteed',
  verdicts: {
    AU: 'No go',
    SG: 'Go',
    US: 'Go'
  }
}, {
  claim: '619% DV vitamin C',
  verdicts: {
    AU: 'No go',
    SG: 'Review',
    US: 'Go'
  }
}, {
  claim: 'Add to smoothies, yogurt or oatmeal',
  verdicts: {
    AU: 'No go',
    SG: 'No go',
    US: 'Go'
  }
}, {
  claim: 'Vegan, gluten-free, non-GMO',
  verdicts: {
    AU: 'Go',
    SG: 'Go',
    US: 'Go'
  }
}];
const foodRows: ClaimCompareRow[] = [{
  claim: 'Formulated Supplementary Food',
  verdicts: {
    AU: 'Review',
    SG: 'Review',
    US: 'Not applicable'
  }
}, {
  claim: 'LOW GI = 36 (Low) made with skim milk',
  verdicts: {
    AU: 'Review',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Made with skim milk',
  verdicts: {
    AU: 'Go',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Contains Gluten, Milk, Soy (allergen declaration)',
  verdicts: {
    AU: 'Go',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Source of calcium',
  verdicts: {
    AU: 'No go',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Source of iron',
  verdicts: {
    AU: 'No go',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Good source of vitamin D',
  verdicts: {
    AU: 'No go',
    SG: 'Go',
    US: 'Not applicable'
  }
}, {
  claim: 'Supports growth and development',
  verdicts: {
    AU: 'No go',
    SG: 'Review',
    US: 'Not applicable'
  }
}];
const foodSummaries: Record<MarketCode, string> = {
  AU: '2 permitted · 2 with conditions · 4 not permitted',
  SG: '5 permitted · 3 with conditions',
  US: '—'
};
const foodClassifications: Record<MarketCode, string> = {
  AU: 'Non-TGA regulated food',
  SG: 'General food',
  US: 'Conventional food'
};
const foodNote = 'All nutrient and health claims fail in Australia because the NPSC score of 15 exceeds the threshold of 4.0. Singapore applies no equivalent nutrient profiling gate.';
interface ClaimsCompareViewProps {
  productName: string;
  onBack: () => void;
  variant?: 'supplement' | 'food';
}
export function ClaimsCompareView({
  productName,
  onBack,
  variant = 'supplement'
}: ClaimsCompareViewProps) {
  const isFood = variant === 'food';
  const rows = isFood ? foodRows : supplementRows;
  const summaries = isFood ? foodSummaries : supplementSummaries;
  const jointlyRejected = rows.filter(row => row.verdicts.AU === 'No go' && row.verdicts.SG === 'No go').map(row => row.claim);
  const differCount = rows.filter(row => new Set(['AU', 'SG'].map(market => row.verdicts[market as MarketCode])).size > 1).length;
  const [selected, setSelected] = useState<MarketCode[]>(['AU', 'SG']);
  const [differencesOnly, setDifferencesOnly] = useState(false);
  const [revealRejected, setRevealRejected] = useState(false);
  const [activeCell, setActiveCell] = useState<{
    claim: string;
    market: MarketCode;
  } | null>(null);
  const toggleMarket = (market: MarketCode) => {
    setActiveCell(null);
    setSelected(current => {
      if (current.includes(market)) {
        return current.length === 1 ? current : current.filter(code => code !== market);
      }
      if (current.length >= 3) return current;
      return selectableMarkets.filter(code => current.includes(code) || code === market);
    });
  };
  const visibleRows = useMemo(() => {
    if (!differencesOnly) return rows;
    return rows.filter(row => {
      if (revealRejected && jointlyRejected.includes(row.claim)) return true;
      return new Set(selected.map(market => row.verdicts[market])).size > 1;
    });
  }, [differencesOnly, revealRejected, selected]);
  const gridTemplate = {
    gridTemplateColumns: `minmax(240px, 1.5fr) repeat(${selected.length}, minmax(150px, 1fr))`
  };
  return <div className="flex flex-1 flex-col overflow-hidden bg-[#fafbf8]" data-id="element-22" data-mp="19hk3zup68x8k">
      <header className="border-b border-[#dce3da] bg-white px-6 py-4 lg:px-10" data-id="element-23" data-mp="14n64bg3h83y3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-forest-green transition hover:underline" data-id="element-24" data-mp="1if47zbef57mv">
          <ArrowLeft className="h-4 w-4" data-id="element-25" data-mp="1sebhfni18i4p" />
          Back to Claims
        </button>
        <p className="mt-3 text-sm text-[#718174]" data-id="element-26" data-mp="1sc8rt4kskr0i">{productName}</p>
        <h1 className="mt-0.5 font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-27" data-mp="1ngjp4sdh6tzz">Claims comparison</h1>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-10" data-id="element-28" data-mp="3u7j7nrz6ok5d">
        <div className="mx-auto max-w-7xl" data-id="element-29" data-mp="3p86aadaoruoj">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-30" data-mp="ipcxqszl5tgr">
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Markets to compare" data-id="element-31" data-mp="mqyh8x6ml5zx">
              {selectableMarkets.map(market => {
              const isSelected = selected.includes(market);
              const atLimit = !isSelected && selected.length >= 3;
              return <button key={market} type="button" onClick={() => toggleMarket(market)} disabled={atLimit} aria-pressed={isSelected} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold transition ${isSelected ? 'border-forest-green bg-forest-green text-white' : atLimit ? 'cursor-not-allowed border-[#dce3da] bg-white text-[#b0b9b1]' : 'border-forest-green/45 bg-white text-forest-green hover:bg-[#f3f8f1]'}`} data-id="element-32" data-mp="11oqpr5dr8u4m">
                    {isSelected && <Check className="h-3 w-3" data-id="element-33" data-mp="2zxoacxweao63" />}
                    {market}
                  </button>;
            })}
              {lockedMarkets.map(market => <Tooltip key={market} content={lockedTooltip} data-id="element-34" data-mp="2v0o6mtn0pxi5">
                  <span className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-dashed border-[#c3ccc4] bg-[#f7f8f6] px-2.5 py-1 font-mono text-[11px] font-semibold text-[#8b968d]" data-id="element-35" data-mp="3sl0m0d10pbkm">
                    <Lock className="h-3 w-3" data-id="element-36" data-mp="3iuxru9i5trkp" />
                    {market}
                  </span>
                </Tooltip>)}
              <span className="ml-1 text-xs text-[#8b968d]" data-id="element-37" data-mp="f75mfd99g0s3">Maximum three markets</span>
            </div>
            <label className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-evergreen" data-id="element-38" data-mp="agyzdhuecoby">
              <input type="checkbox" checked={differencesOnly} onChange={event => setDifferencesOnly(event.target.checked)} className="h-4 w-4 rounded border-[#c3ccc4] text-forest-green focus:ring-forest-green" data-id="element-39" data-mp="3ey07rvzpo8ly" />
              Differences only
            </label>
          </div>

          <section className="mt-5 overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-40" data-mp="3auu7r636zdnx">
            <div className="overflow-x-auto" data-id="element-41" data-mp="2faiei5n6fe5">
              <div className="min-w-[700px]" data-id="element-42" data-mp="6hp0h01hlb4d">
                <div className="grid items-center border-b border-[#e5ebe3] bg-[#fbfdf9]" style={gridTemplate} data-id="element-43" data-mp="6gdwaz44q1cq">
                  <p className="px-5 py-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-44" data-mp="3tnfw75brs1yh">Classification</p>
                  {selected.map(market => <div key={market} className="px-5 py-4" data-id="element-45" data-mp="2dbixzbnfvtvs">
                      <p className="text-sm font-medium text-evergreen" data-id="element-46" data-mp="2i92g4mn9cqt0">{market === 'SG' ? `Singapore (${isFood ? 'SFA' : 'HSA'})` : marketMeta[market].name}</p>
                      <p className="mt-0.5 text-xs text-[#718174]" data-id="element-47" data-mp="25q6q09zpstqe">{isFood ? foodClassifications[market] : marketMeta[market].classification}</p>
                    </div>)}
                </div>
                {isFood ? <p className="border-b border-[#e5ebe3] bg-amber-50 px-5 py-3 text-xs leading-relaxed text-amber-900" data-id="element-48" data-mp="2eqv5l94rb25e">{foodNote}</p> : <p className="border-b border-[#e5ebe3] px-5 py-3 text-xs leading-relaxed text-[#718174]" data-id="element-49" data-mp="1mzfhfanjx7d2">
                    Australia operates a closed permissible-indications list; Singapore and the United States assess claims against general advertising rules. Several rows differ on that basis alone.
                  </p>}

                <div className="divide-y divide-[#edf0eb]" data-id="element-50" data-mp="188opm2lym89y">
                  {visibleRows.length > 0 ? visibleRows.map(row => <div key={row.claim} className="grid items-center" style={gridTemplate} data-id="element-51" data-mp="o9sj2kp30uzj">
                        <p className="px-5 py-4 text-sm font-semibold text-evergreen" data-id="element-52" data-mp="j9o5p3jh0gdj">{row.claim}</p>
                        {selected.map(market => {
                    const isActive = activeCell?.claim === row.claim && activeCell.market === market;
                    return <div key={market} className="px-5 py-4" data-id="element-53" data-mp="5awtxns2z6sk">
                              <button type="button" onClick={() => setActiveCell({
                        claim: row.claim,
                        market
                      })} className={`rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-green ${isActive ? 'ring-2 ring-forest-green ring-offset-1' : ''}`} data-id="element-54" data-mp="5c7yhptcoikq">
                                <VerdictPill verdict={row.verdicts[market]} data-id="element-55" data-mp="3ir46sneni3rt" />
                              </button>
                            </div>;
                  })}
                      </div>) : <p className="px-5 py-10 text-center text-sm text-[#718174]" data-id="element-56" data-mp="1y5flnnoem6j7">All selected markets agree on every claim.</p>}
                </div>

                <div className="grid items-start border-t border-[#e5ebe3] bg-[#fbfdf9]" style={gridTemplate} data-id="element-57" data-mp="276vwtxn1mcoh">
                  <div className="px-5 py-4" data-id="element-58" data-mp="1aizbygkkov7g">
                    <p className="text-sm font-semibold text-evergreen" data-id="element-59" data-mp="1ej9hjs4gdjls">
                      {differCount} of {rows.length} rows differ{isFood ? ' · Australia blocks 4 claims on NPSC alone' : ''}
                    </p>
                    {!isFood && <button type="button" onClick={() => setRevealRejected(true)} className="mt-1 text-left text-sm font-medium text-amber-700 hover:underline" data-id="element-60" data-mp="1oano2jutnjof">
                        Both AU and SG reject {jointlyRejected.length} claim
                      </button>}
                  </div>
                  {selected.map(market => <p key={market} className="px-5 py-4 font-mono text-xs leading-relaxed text-[#617366]" data-id="element-61" data-mp="27vrbuh4ydslr">{summaries[market]}</p>)}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-[#dce3da] bg-white px-5 py-4 shadow-sm" aria-live="polite" data-id="element-62" data-mp="1g5uy3smq3ena">
            {activeCell ? <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-63" data-mp="1p5i28m6u6yk1">
                <div data-id="element-64" data-mp="1k6ew0yf4emah">
                  <p className="text-sm font-semibold text-evergreen" data-id="element-65" data-mp="1k5x1q7c0qi57">{activeCell.claim}</p>
                  <p className="mt-1 text-sm text-[#718174]" data-id="element-66" data-mp="30kevy9dyubxq">
                    {marketMeta[activeCell.market].name} · {rows.find(row => row.claim === activeCell.claim)?.verdicts[activeCell.market]}
                  </p>
                </div>
                <button type="button" className="self-start text-sm font-semibold text-forest-green hover:underline sm:self-auto" data-id="element-67" data-mp="341181m717xi7">
                  Open the {marketMeta[activeCell.market].name} claims assessment →
                </button>
              </div> : <p className="text-sm text-[#718174]" data-id="element-68" data-mp="2z4kj4ps0df42">Select a cell to see the market-specific detail.</p>}
          </section>
        </div>
      </div>
    </div>;
}
function VerdictPill({
  verdict
}: {
  verdict: ClaimVerdict;
}) {
  const style: Record<ClaimVerdict, string> = {
    Go: 'bg-forest-green/10 text-forest-green',
    Review: 'bg-amber-100 text-amber-800',
    'No go': 'bg-red-100 text-red-800',
    'Not applicable': 'bg-[#f4f6f3] text-[#9aa59c]'
  };
  const label: Record<ClaimVerdict, string> = {
    Go: 'Permitted',
    Review: 'Permitted with conditions',
    'No go': 'Not permitted',
    'Not applicable': 'Not applicable'
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${style[verdict]}`} data-id="element-69" data-mp="2lwvbbylhxmy1">{label[verdict]}</span>;
}